import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useContaDigital } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { formatCurrency, formatShortDateWithoutTimezone } from '@ibk/shared-utils';
import { ExtractType, TransactionFlow, TransactionType } from '../../enums';
import { useGetExtract } from '../useGetExtract';
import { useGetScheduledExtract } from '../useGetScheduledExtract';
export function useExtract(canSeeBalance, canSeeStatements) {
    const { t, currentLanguage } = useTranslation();
    const { data: contaDigitalData, isLoading: balanceLoading } = useContaDigital({ enabled: !!canSeeBalance });
    const showBalance = useAppSelector(state => state.showBalance.showBalance);
    const { extract: pastExtract, setExtract: setPastExtract, loading: pastLoading, setLoading: setPastLoading, error: pastError, loadError: pastLoadError, setError: setPastError, getExtract: getPastExtract, } = useGetExtract(canSeeStatements);
    const { extract: scheduledExtract, setExtract: setScheduledExtract, loading: scheduledLoading, setLoading: setScheduledLoading, error: scheduledError, setError: setScheduledError, getScheduledExtract, } = useGetScheduledExtract(canSeeStatements);
    const initialFilters = useRef((() => {
        return {
            flow: undefined,
            type: undefined,
            startAt: undefined,
            endAt: undefined,
            minAmount: undefined,
            maxAmount: undefined,
        };
    })());
    const [filters, setFilters] = useState(initialFilters.current);
    const [extractType, setExtractType] = useState(ExtractType.PAST);
    const [showReceiptModal, setShowReceiptModal] = useState(undefined);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showExportExtractModal, setShowExportExtractModal] = useState(false);
    const [showAlert, setShowAlert] = useState(undefined);
    const [monthIncome, setMonthIncome] = useState();
    const [monthOutcome, setMonthOutcome] = useState();
    const getPastExtractRef = useRef(getPastExtract);
    const getScheduledExtractRef = useRef(getScheduledExtract);
    useEffect(() => {
        getPastExtractRef.current = getPastExtract;
        getScheduledExtractRef.current = getScheduledExtract;
    }, [getPastExtract, getScheduledExtract]);
    function parseCurrencyToNumber(value) {
        if (!value)
            return 0;
        const normalized = value
            .replace(/[^\d,-]/g, '')
            .replace(/\./g, '')
            .replace(',', '.');
        return Number(normalized) || 0;
    }
    const getExtract = useCallback((type) => {
        setPastLoading(false);
        setPastError(false);
        setPastExtract(undefined);
        setScheduledLoading(false);
        setScheduledError(false);
        setScheduledExtract(undefined);
        if (type === ExtractType.SCHEDULED) {
            getScheduledExtractRef.current(filters?.startAt, filters?.endAt);
        }
        else {
            getPastExtractRef.current(filters?.flow, filters?.type, filters?.startAt, filters?.endAt, filters?.minAmount, filters?.maxAmount);
        }
    }, [
        setPastLoading,
        setPastError,
        setPastExtract,
        setScheduledLoading,
        setScheduledError,
        setScheduledExtract,
        filters?.flow,
        filters?.type,
        filters?.startAt,
        filters?.endAt,
        filters?.minAmount,
        filters?.maxAmount,
    ]);
    useEffect(() => {
        if (canSeeStatements) {
            getExtract(extractType);
        }
    }, [
        filters?.flow,
        filters?.type,
        filters?.startAt,
        filters?.endAt,
        filters?.minAmount,
        filters?.maxAmount,
        canSeeStatements,
        extractType,
        canSeeBalance,
        getExtract,
    ]);
    useEffect(() => {
        if (pastExtract && (!monthIncome || !monthOutcome)) {
            const transactions = pastExtract.reduce((acc, group) => acc.concat(group.data), []);
            if (!monthIncome)
                setMonthIncome(formatCurrency(transactions
                    .filter(t => t.flow === TransactionFlow.CASH_IN || t.flow === TransactionFlow.REVERSAL_IN)
                    .reduce((acc, t) => acc + parseCurrencyToNumber(t.amount), 0)));
            if (!monthOutcome)
                setMonthOutcome(formatCurrency(transactions
                    .filter(t => t.flow === TransactionFlow.CASH_OUT || t.flow === TransactionFlow.REVERSAL_OUT)
                    .reduce((acc, t) => acc + parseCurrencyToNumber(t.amount), 0)));
        }
    }, [pastExtract, monthIncome, monthOutcome]);
    const startAtEndAtFilters = useMemo(() => {
        if (filters?.startAt &&
            filters?.endAt &&
            filters?.startAt !== initialFilters.current.startAt &&
            filters?.endAt !== initialFilters.current.endAt) {
            return `${formatShortDateWithoutTimezone(filters?.startAt, currentLanguage)} ${t('to')} ${formatShortDateWithoutTimezone(filters?.endAt, currentLanguage)}`;
        }
    }, [filters?.startAt, filters?.endAt, currentLanguage, t]);
    const minAmountMaxAmountFilters = useMemo(() => {
        if (extractType === ExtractType.PAST) {
            if (filters?.maxAmount && !filters.minAmount) {
                return `${t('until')} ${formatCurrency(filters?.maxAmount)}`;
            }
            if (filters.minAmount && !filters.maxAmount) {
                return `${t('from')} ${formatCurrency(filters?.minAmount)}`;
            }
            if (filters.minAmount && filters.maxAmount) {
                return `${formatCurrency(filters?.minAmount)} ${t('to')} ${formatCurrency(filters?.maxAmount)}`;
            }
        }
    }, [extractType, filters?.minAmount, filters?.maxAmount, t]);
    const typeFilter = useMemo(() => {
        if (extractType === ExtractType.PAST && filters?.type) {
            return {
                [TransactionType.PIX]: t('pix'),
                [TransactionType.BILLET]: t('billet'),
                [TransactionType.TED]: t('ted'),
                [TransactionType.P2P]: t('transfer'),
            }[filters?.type];
        }
    }, [extractType, filters?.type, t]);
    const hasFilters = useMemo(() => {
        return Boolean(startAtEndAtFilters || minAmountMaxAmountFilters || filters.flow || typeFilter);
    }, [startAtEndAtFilters, minAmountMaxAmountFilters, filters.flow, typeFilter]);
    const loading = useMemo(() => scheduledLoading || pastLoading, [scheduledLoading, pastLoading]);
    const error = useMemo(() => scheduledError || pastError, [scheduledError, pastError]);
    const extract = useMemo(() => {
        if (!loading && !error) {
            return extractType === ExtractType.SCHEDULED ? scheduledExtract : pastExtract;
        }
    }, [scheduledExtract, pastExtract, loading, error, extractType]);
    const loadError = useMemo(() => pastLoadError && !loading && extractType === ExtractType.PAST, [pastLoadError, loading, extractType]);
    const noResultForFiltersError = useMemo(() => hasFilters && !extract && !loading && !loadError, [hasFilters, extract, loading, loadError]);
    const noResultError = useMemo(() => error && !loading && !hasFilters && !extract && !loadError, [error, loading, hasFilters, extract, loadError]);
    const balance = useMemo(() => {
        const amount = typeof contaDigitalData?.balance === 'object' && contaDigitalData?.balance !== null
            ? (contaDigitalData.balance.amount ?? 0)
            : 0;
        return formatCurrency(amount);
    }, [contaDigitalData]);
    const getBalanceValue = () => {
        if (!showBalance)
            return '*****';
        if (balanceLoading)
            return undefined;
        return balance;
    };
    function reset() {
        if (hasFilters) {
            setFilters({ ...initialFilters.current });
        }
        else {
            getExtract(extractType);
        }
    }
    function retry() {
        getExtract(extractType);
    }
    function filter(partial) {
        setFilters(previous => ({ ...previous, ...partial }));
    }
    function updateExtractType(type) {
        setExtractType(type);
        if (hasFilters) {
            reset();
        }
        else {
            getExtract(type);
        }
    }
    return {
        monthIncome,
        monthOutcome,
        balance,
        balanceLoading,
        extract,
        loading,
        noResultError,
        noResultForFiltersError,
        loadError,
        startAtEndAtFilters,
        minAmountMaxAmountFilters,
        typeFilter,
        hasFilters,
        extractType,
        setExtractType,
        initialFilters: initialFilters.current,
        showReceiptModal,
        setShowReceiptModal,
        showFiltersModal,
        setShowFiltersModal,
        filters,
        setFilters,
        reset,
        retry,
        filter,
        updateExtractType,
        showExportExtractModal,
        setShowExportExtractModal,
        showAlert,
        setShowAlert,
        getBalanceValue,
    };
}
